import React, { FC } from 'react';
import { ReloadOutlined, CheckOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { Button, Form, Input, Radio, SelectProps } from 'antd';
import { CustomInput } from '../../../../../../../common/components/CustomInput/CustomInput';
import { CustomTextArea } from '../../../../../../../common/components/CustomTextArea/CustomTextArea';
import { CustomSwitch } from '../../../../../../../common/components/CustomSwitch/CustomSwitch';
import { CustomSelect } from '../../../../../../../common/components/CustomSelect/CustomSelect';
import { ICurrentEnglishName } from '../../../../../../usersPage/interfaces/interfaces';
import { getValidateCase } from '../../../../../helpers/helpers';
import Types from '../../../../../../../../types/types';

// eslint-disable-next-line max-len
import s from '../XPathBlock.module.scss';
const { errorEngNameMessage, labelNames } = Types;

interface IFd {
  currentEnglishName: string;
  currentAttributeType: string;
  currentXmlPath: string;
  currentParentCode: string;
  currentMandatoryPlurality: string;
  currentOidDirectory: string;
  currentSection: string;
  currentDescription: string;
  currentIsParent: boolean;
  currentResult: string;
  showAllSettings: boolean;
  key: string;
}
interface IXPathContentChildren {
  attributeTypeOptions: Array<{ name: string; value: number }>;
  parentCodeOptions: Array<{ label: string; value: string }>;
  fd: IFd;
  hasChangeXPath: boolean;
  setHasChangeXPath: (has: boolean) => unknown;
  xPathResponse: Array<string> | string;
  handleChangeFormData: (key: string, value: string | boolean) => void;
  handleValidationXPathCheck: () => void;
  handleSaveXPath: () => void;
  setError: (err: string) => void;
  deleteError: (err: string) => void;
}
const parentOptions: SelectProps['options'] = [];
for (let i = 10; i < 36; i++) {
  parentOptions.push({
    value: i.toString(36) + i,
    label: i.toString(36) + i,
  });
}

const getRespType = (response, handleValidationXPathCheck) => {
  const handleClick = (e) => {
    e.preventDefault();
    handleValidationXPathCheck();
  };
  switch (response) {
    case 'reload': {
      return <ReloadOutlined onClick={handleClick} style={{ color: '#00A899' }} />;
    }
    case 'error': {
      return <CloseCircleOutlined style={{ color: '#F74A5C' }} />;
    }
    case 'correct': {
      return <CheckOutlined style={{ color: '#14AE5C' }} />;
    }

    default: {
      return <ReloadOutlined onClick={handleClick} style={{ color: '#00A899', zIndex: 4 }} />;
    }
  }
};
const getBacColor = (response) => {
  switch (response) {
    case 'reload': {
      return '#D9D9D9';
    }
    case 'error': {
      return 'rgba(247, 74, 92, 0.06)';
    }
    case 'correct': {
      return '#F1FAF5';
    }

    default: {
      return '#D9D9D9';
    }
  }
};
const getResponse = (response, handleValidationXPathCheck, hasChangeXPath) => {
  let resp = '';
  if (Array.isArray(response) && response.length) {
    resp = 'correct';
  } else if (response === 'error') {
    resp = 'error';
  }
  if (hasChangeXPath) {
    resp = 'reload';
  }
  return (
    <div
      onClick={(e) => {
        e.preventDefault();
      }}
      className={s.xPathResponse}
      style={{ background: getBacColor(resp) }}
    >
      <div>Значение: </div>
      <div style={{ display: 'flex' }}>
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/*@ts-ignore*/}
        <ul type={'circle'}>
          {Array.isArray(response) && response.length
            ? response.map((el, index) => (
                <li key={index} style={{ marginRight: 12 }}>
                  {Array.isArray(el) ? (
                    <ol>
                      {el.map((text, index) => (
                        <li key={index} style={{ marginRight: 12 }}>
                          {text}
                        </li>
                      ))}
                    </ol>
                  ) : (
                    el
                  )}
                </li>
              ))
            : ''}
        </ul>

        <div
          onClick={(e) => {
            e.preventDefault();
          }}
        >
          {getRespType(resp, handleValidationXPathCheck)}
        </div>
      </div>
    </div>
  );
};

const optionsMandatoryPlurality = [
  { label: '0..1', value: '0..1' },
  { label: '0..*', value: '0..*' },
  { label: '1..1', value: '1..1' },
  { label: '1..*', value: '1..*' },
];
export const XPathContentChildren: FC<IXPathContentChildren> = ({
  parentCodeOptions,
  attributeTypeOptions,
  fd,
  handleChangeFormData,
  xPathResponse,
  handleValidationXPathCheck,
  hasChangeXPath,
  setHasChangeXPath,
  handleSaveXPath,
  setError,
  deleteError,
}) => {
  const handleChangeXPath = (name, value) => {
    handleChangeFormData(name, value);
    setHasChangeXPath(true);
  };

  const handleCheckXPath = () => {
    handleValidationXPathCheck();
    setHasChangeXPath(false);
  };
  const parentCodeOptionsWithDisabled = (parentCodeOptions || [])
    .filter((code) => code.value !== fd.currentEnglishName)
    .map((code) => {
      if (code.value === fd.currentParentCode) {
        return { ...code, disabled: true };
      } else return code;
    });
  const click = (e) => e.stopPropagation();
  return (
    <div className={s.XPathBlock}>
      {/*<div className={s.XPathBlockForm} layout='vertical'>*/}
      <div className={s.XPathBlockForm}>
        <div className={s.content}>
          <div className={s.fieldsWrapper}>
            <Form.Item<ICurrentEnglishName>
              name={'currentEnglishName'}
              label={labelNames['englishName']}
              initialValue={fd.currentEnglishName}
              rules={[
                // {
                //   required: true,
                //   message: 'Обязательно для заполнения',
                // },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value) {
                      setError(fd.key);
                      return Promise.reject(new Error('Обязательно для заполнения'));
                    }
                    const cond = getValidateCase(getFieldValue('currentEnglishName'), 'en');
                    if (cond) {
                      deleteError(fd.key);
                      handleChangeFormData('currentEnglishName', value);
                      return Promise.resolve();
                    }
                    setError(fd.key);
                    return Promise.reject(new Error(errorEngNameMessage));
                  },
                }),
              ]}
              className={s.field}
            >
              <Input
                onBlur={handleSaveXPath}
                onClick={(event) => {
                  event.stopPropagation();
                }}
              />
            </Form.Item>

            {fd.currentIsParent ? (
              <Form.Item
                label='Обязательность\множественность'
                name='mandatory_plurality'
                className={`${s.field} ${s.mandatoryPlurality}`}
                initialValue={fd.currentMandatoryPlurality}
                /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
                // @ts-ignore
                onClick={click}
              >
                <Radio.Group
                  onBlur={handleSaveXPath}
                  options={optionsMandatoryPlurality}
                  onChange={(e) =>
                    handleChangeFormData('currentMandatoryPlurality', e.target.value)
                  }
                  value={fd.currentMandatoryPlurality}
                ></Radio.Group>
              </Form.Item>
            ) : (
              <CustomSelect
                label={'Тип атрибута'}
                name={'currentAttributeType'}
                value={fd.currentAttributeType}
                handleChange={handleChangeFormData}
                handleSaveXPath={handleSaveXPath}
                options={attributeTypeOptions}
                fieldNames={{ label: 'name', value: 'name' }}
              />
            )}
          </div>

          <div>
            <CustomTextArea
              label={'Xml путь'}
              name={'currentXmlPath'}
              value={fd.currentXmlPath}
              handleChange={handleChangeXPath}
              handleSaveXPath={handleSaveXPath}
            />
            {getResponse(xPathResponse, handleCheckXPath, hasChangeXPath)}
          </div>

          <div className={s.fieldsWrapper}>
            <div style={{ flex: '1 1' }}>
              <CustomSelect
                label={'Родительский код'}
                name={'currentParentCode'}
                value={fd.currentParentCode}
                handleChange={handleChangeFormData}
                handleSaveXPath={handleSaveXPath}
                options={parentCodeOptionsWithDisabled}
                fieldNames={{ label: 'label', value: 'value' }}
              />
            </div>

            {fd.currentIsParent ? (
              <CustomSwitch
                label={'Родительский'}
                name={'currentIsParent'}
                value={fd.currentIsParent}
                handleChange={handleChangeFormData}
              />
            ) : (
              <Form.Item
                label='Обязательность\множественность'
                name='mandatory_plurality'
                className={`${s.field} ${s.mandatoryPlurality}`}
                initialValue={fd.currentMandatoryPlurality}
                /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
                // @ts-ignore
                onClick={click}
              >
                <Radio.Group
                  onBlur={handleSaveXPath}
                  options={optionsMandatoryPlurality}
                  onChange={(e) =>
                    handleChangeFormData('currentMandatoryPlurality', e.target.value)
                  }
                  value={fd.currentMandatoryPlurality}
                ></Radio.Group>
              </Form.Item>
            )}
          </div>
          {!fd.currentIsParent ? (
            <div>
              {fd.showAllSettings ? (
                <div>
                  <div className={s.fieldsWrapper}>
                    <CustomInput
                      label={'oid справочника'}
                      name={'currentOidDirectory'}
                      value={fd.currentOidDirectory}
                      handleChange={handleChangeFormData}
                      handleSaveXPath={handleSaveXPath}
                    />

                    <CustomInput
                      label={'Секция'}
                      name={'currentSection'}
                      value={fd.currentSection}
                      handleChange={handleChangeFormData}
                      handleSaveXPath={handleSaveXPath}
                    />
                  </div>

                  <div className={s.fieldsWrapper}>
                    <CustomInput
                      label={'Описание'}
                      name={'currentDescription'}
                      value={fd.currentDescription}
                      handleChange={handleChangeFormData}
                      handleSaveXPath={handleSaveXPath}
                    />

                    <CustomSwitch
                      label={'Родительский'}
                      name={'currentIsParent'}
                      value={fd.currentIsParent}
                      handleChange={handleChangeFormData}
                    />
                  </div>
                </div>
              ) : (
                ''
              )}
              <div className={s.showAllSettingsWrapper}>
                <Button
                  type={'link'}
                  onClick={() => handleChangeFormData('showAllSettings', !fd.showAllSettings)}
                >
                  {fd.showAllSettings ? 'Свернуть' : 'Дополнительно'}
                </Button>
              </div>{' '}
            </div>
          ) : (
            ''
          )}
        </div>
      </div>
    </div>
  );
};
